import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarPlus, Check, Loader2 } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeading } from "@/components/site/Section";
import { EmptyState, Loading, ProblemAlert } from "@/components/site/Feedback";
import { ManageOnWebsite } from "@/components/site/Membership";
import { AppointmentsList, useBookingFlow, useMentorshipOptions } from "@/components/site/Booking";
import { api } from "@/lib/api";
import { useAuth, useCheckout } from "@/lib/hooks";
import { isMobile } from "@/lib/config";
import { minutesLabel, money } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CreditPackageDto } from "@/lib/types";

// Not an #anchor link: the mobile build uses hash routing.
const scrollToBooking = () => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });

export function MentorshipPage() {
  usePageTitle("Mentorship & Schemes");
  const { isAuthenticated } = useAuth();
  const packages = useQuery({ queryKey: ["mentorship", "packages"], queryFn: api.mentorship.packages });

  return (
    <>
      <PageHero
        eyebrow="Mentorship & schemes"
        title="Choose the level of guidance you need"
        subtitle="Every scheme is a one-off upfront upgrade that sits on top of your portal membership. Each package adds mentorship time to your balance — buy more whenever you need them."
      >
        {isAuthenticated ? (
          <Button variant="gold" size="xl" onClick={scrollToBooking}>
            Book a session
          </Button>
        ) : (
          <Button asChild variant="gold" size="xl">
            <Link to="/signup">Start with the portal</Link>
          </Button>
        )}
      </PageHero>

      {isAuthenticated && <MemberMentorship />}

      <Section>
        <SectionHeading
          eyebrow="Scheme packages"
          title="Mentorship packages"
          subtitle="Your mentorship time never expires while your account is open. Book it as 30, 60 or 90-minute sessions."
        />
        <div className="mt-12">
          {packages.isPending ? (
            <Loading />
          ) : packages.isError ? (
            <ProblemAlert error={packages.error} />
          ) : packages.data.length === 0 ? (
            <EmptyState title="Packages coming soon" />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {[...packages.data]
                .sort((a, b) => a.order - b.order)
                .map((p) => (
                  <PackageCard key={p.id} pkg={p} />
                ))}
            </div>
          )}
        </div>
        {isMobile && <ManageOnWebsite className="mt-8">Packages are purchased on our website.</ManageOnWebsite>}
      </Section>

      <Section ink className="py-20">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionHeading
            light
            eyebrow="Not ready for a scheme?"
            title="Book a single Strategy Hour"
            subtitle="Sixty focused minutes on your position: current deals, financing, or the next step in your portfolio. Uses one hour of your mentorship time."
          />
          <div className="flex lg:justify-end">
            {isAuthenticated ? (
              <Button variant="gold" size="xl" onClick={scrollToBooking}>
                Book a Strategy Hour
              </Button>
            ) : (
              <Button asChild variant="gold" size="xl">
                <Link to="/login" search={{ redirect: "/mentorship" }}>
                  Sign in to book
                </Link>
              </Button>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}

function PackageCard({ pkg }: { pkg: CreditPackageDto }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const checkout = useCheckout();

  const buy = () => {
    if (!isAuthenticated) {
      void navigate({ to: "/login", search: { redirect: "/mentorship" } });
      return;
    }
    checkout.mutate({ kind: "package", id: pkg.id });
  };

  return (
    <div
      className={
        pkg.isFeatured
          ? "flex flex-col rounded-lg border-2 border-gold bg-card p-9 shadow-elevated"
          : "flex flex-col rounded-lg border border-border bg-card p-9 shadow-card"
      }
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <p className="eyebrow text-gold">{pkg.name}</p>
          {pkg.subtitle && <p className="mt-2 text-sm text-muted-foreground">{pkg.subtitle}</p>}
        </div>
        <p className="font-display text-4xl">{money(pkg.price, pkg.currency)}</p>
      </div>
      <div className="hairline-gold my-7" />
      {pkg.description && <p className="mb-4 text-sm text-muted-foreground">{pkg.description}</p>}
      <ul className="flex-1 space-y-3">
        {pkg.features.map((i) => (
          <li key={i} className="flex items-start gap-3 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-gold" />
            {i}
          </li>
        ))}
        <li className="flex items-start gap-3 text-sm text-muted-foreground">
          <Check className="mt-0.5 size-4 shrink-0 text-gold" />
          {minutesLabel(pkg.credits)} of mentorship time
        </li>
      </ul>
      {!isMobile && (
        <Button
          variant={pkg.isFeatured ? "gold" : "outlineGold"}
          size="lg"
          className="mt-8 w-full"
          disabled={checkout.isPending}
          onClick={buy}
        >
          {checkout.isPending && <Loader2 className="animate-spin" />}
          Buy {pkg.name}
        </Button>
      )}
    </div>
  );
}

/** Credits, booking and the member's appointments. */
function MemberMentorship() {
  const credits = useQuery({ queryKey: ["billing", "credits"], queryFn: api.billing.credits });
  const options = useMentorshipOptions();
  const flow = useBookingFlow();
  const [topic, setTopic] = useState("");
  const [minutes, setMinutes] = useState<number | null>(null);
  const balance = credits.data?.mentorship ?? 0;
  const lengths = options.data?.sessionLengths ?? [30, 60, 90];
  const affordable = lengths.filter((l) => l <= balance);
  // Default: the longest session the balance covers, at most 60 minutes.
  const selected = minutes !== null && affordable.includes(minutes) ? minutes : (affordable.filter((l) => l <= 60).at(-1) ?? affordable[0] ?? null);

  return (
    <Section className="pb-0">
      <div id="book" className="grid scroll-mt-24 gap-8 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-lg border border-gold bg-card p-8 shadow-elevated">
          <p className="eyebrow text-gold">Your mentorship time</p>
          <p className="mt-3 font-display text-6xl">{credits.isPending ? "…" : minutesLabel(balance)}</p>
          <p className="text-sm text-muted-foreground">available to book</p>
          {selected !== null ? (
            <div className="mt-6 space-y-3">
              <div className="space-y-2">
                <Label>Session length</Label>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Session length">
                  {lengths.map((l) => (
                    <button
                      key={l}
                      type="button"
                      role="radio"
                      aria-checked={selected === l}
                      disabled={l > balance}
                      onClick={() => setMinutes(l)}
                      className={cn(
                        "rounded-md border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                        selected === l ? "border-gold bg-gold/15 font-medium" : "border-border hover:bg-accent",
                      )}
                    >
                      {l} min
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="topic">What would you like to cover? (optional)</Label>
                <Input id="topic" maxLength={500} value={topic} placeholder="e.g. Financing my first HMO" onChange={(e) => setTopic(e.target.value)} />
              </div>
              <Button variant="gold" size="lg" className="w-full" disabled={flow.busy} onClick={() => flow.book.mutate({ topic: topic.trim() || null, minutes: selected })}>
                {flow.book.isPending ? <Loader2 className="animate-spin" /> : <CalendarPlus />}
                Book a {selected}-minute session
              </Button>
            </div>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">
              {isMobile
                ? balance > 0
                  ? "Your remaining time is shorter than the shortest session. Packages are available on our website."
                  : "You have no mentorship time. Packages are available on our website."
                : balance > 0
                  ? "Your remaining time is shorter than the shortest session. Add a package below to book again."
                  : "You have no mentorship time yet. Choose a package below to book your first session."}
            </p>
          )}
        </div>
        <div>
          <h3 className="text-3xl">My appointments</h3>
          <div className="hairline-gold mb-6 mt-4" />
          <AppointmentsList flow={flow} />
        </div>
      </div>
      {flow.dialog}
    </Section>
  );
}
